import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ComponentSync, Grid, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { _keys, BadImplementationException, formatTimestamp, sortArray } from '@nu-art/ts-common';
import { ModuleFE_Book, ModuleFE_Reference, ModuleFE_Tag, ModuleFE_Value, ModuleFE_Variable, resolveReferenceDisplay, } from '@app/hcs-frontend';
import './Printable_DiseaseProfile.scss';
import { CriticalityLabelMap, LikelihoodLabelMap, SeverityLabelMap } from '@app/dp-shared';
import { ConfigKeyFE_DPViewOrder, ModuleFE_DiseaseProfile, ModuleFE_DiseaseValue, ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../_entity.js';
import { resolveValueDisplay } from '@app/hcs-shared';
export class Printable_DiseaseProfile extends ComponentSync {
    // ######################## Static ########################
    static defaultProps = {
        modules: [ModuleFE_Book, ModuleFE_Value]
    };
    // ######################## Life Cycle ########################
    deriveStateFromProps(nextProps, state) {
        state ??= this.state ? { ...this.state } : {};
        state.diseaseProfile ??= ModuleFE_DiseaseProfile.cache.unique(nextProps.diseaseProfileId);
        if (!state.diseaseProfile)
            throw new BadImplementationException(`Could not find DP with id ${nextProps.diseaseProfileId}`);
        return state;
    }
    // ######################## Render - Structure ########################
    render() {
        return _jsxs(LL_V_L, { id: 'printable__disease-profile', children: [_jsxs("table", { children: [_jsx("tbody", { children: _jsx("tr", { children: _jsxs("td", { children: [this.renderTitle(), this.renderGeneralInformation(), this.renderDiseaseViews()] }) }) }), _jsx("tfoot", { children: _jsx("tr", { children: _jsx("td", { children: "\u00A0" }) }) })] }), this.renderFooter()] });
    }
    renderTitle = () => {
        return _jsxs(LL_H_C, { className: 'printable__disease-profile__title', children: [_jsx("h1", { children: this.state.diseaseProfile.label }), _jsxs("p", { children: ["PDF produced on ", formatTimestamp('MMM. D, YYYY', Date.now())] })] });
    };
    renderFooter = () => {
        return _jsxs("div", { className: 'printable__disease-profile__footer', children: [_jsx("span", { className: 'printable__disease-profile__footer__disease-name', children: this.state.diseaseProfile.label }), _jsx("span", { className: 'printable__disease-profile__footer__copyright', children: "Quai.MD \u00A9 Copyright 2023" })] });
    };
    numberArrayRenderer = (arr, multiplier = 1) => {
        if (!arr)
            return undefined;
        if (arr[0] === arr[1])
            return `${arr[0] * multiplier}`;
        return `${arr[0] * multiplier} - ${arr[1] * multiplier}`;
    };
    // ######################## Render - General Information ########################
    renderGeneralInformation = () => {
        return _jsxs(Grid, { className: 'printable__disease-profile__general-information', children: [_jsx("h2", { children: "General Information" }), this.renderOverview(), this.renderEpidemiology(), this.renderPrognosis(), this.renderScores(), this.renderReferences()] });
    };
    renderOverview = () => {
        const dp = this.state.diseaseProfile;
        const variantGroup = ModuleFE_Tag.cache.unique(dp.variantsGroupId);
        return _jsxs(_Fragment, { children: [_jsx("h3", { children: "Overview" }), _jsx("h4", { children: "Label" }), _jsx("p", { children: dp.label ?? '-' }), _jsx("h4", { children: "Profile Abbreviation" }), _jsx("p", { children: dp.initials ?? '-' }), _jsx("h4", { children: "Variant Group" }), _jsx("p", { children: variantGroup?.label ?? '-' }), _jsx("h4", { children: "Tags" }), _jsx("p", { children: "-" }), _jsx("h4", { children: "General Description" }), _jsx("p", { children: dp.profileDescription ?? '-' }), _jsx("h4", { children: "General Notes" }), _jsx("p", { children: dp.profileNotes ?? '-' })] });
    };
    renderEpidemiology = () => {
        const dp = this.state.diseaseProfile;
        return _jsxs(_Fragment, { children: [_jsx("h3", { children: "Epidemiology" }), _jsx("h4", { children: "Epidemiology Notes" }), _jsx("p", { children: dp.epidemiologyNotes ?? '-' })] });
    };
    renderPrognosis = () => {
        const dp = this.state.diseaseProfile;
        return _jsxs(_Fragment, { children: [_jsx("h3", { children: "Prognosis" }), _jsx("h4", { children: "Criticality" }), _jsx("p", { children: dp.criticality ? CriticalityLabelMap[dp.criticality] : '-' })] });
    };
    renderScores = () => {
        const dp = this.state.diseaseProfile;
        return _jsxs(_Fragment, { children: [_jsx("h3", { children: "Scores" }), _jsx("h4", { children: "Scores" }), _jsx("p", { children: dp.scores ?? '-' })] });
    };
    renderReferences = () => {
        const dp = this.state.diseaseProfile;
        return _jsxs(_Fragment, { children: [_jsx("h3", { children: "References" }), dp.referenceIds.map((refId, i) => {
                    const ref = ModuleFE_Reference.cache.unique(refId);
                    return _jsxs(React.Fragment, { children: [_jsxs("h4", { children: [i + 1, ".", ref.type] }), _jsx("p", { children: resolveReferenceDisplay(ref) })] }, refId);
                })] });
    };
    // ######################## Render - MultiVars ########################
    renderDiseaseViews = () => {
        const dp = this.state.diseaseProfile;
        const viewOrder = ConfigKeyFE_DPViewOrder.get();
        const diseaseViews = sortArray(ModuleFE_DiseaseView.cache.allMutable(), i => viewOrder.indexOf(i._id));
        return diseaseViews.map(dv => {
            const mvs = ModuleFE_MultiVar.cache.filter(i => i.diseaseProfileId === dp._id
                && ModuleFE_DiseaseView.filterMultiVarByEnforcedTag(dv, i));
            if (!mvs.length)
                return;
            return _jsxs(Grid, { className: 'printable__disease-profile__view', children: [_jsx("h2", { children: dv.label }), mvs.map(i => this.renderMultiVar(i))] }, dv._id);
        });
    };
    renderMultiVar = (mv) => {
        const dp = this.state.diseaseProfile;
        const mainVar = ModuleFE_Variable.cache.unique(mv._firstVarId);
        const referenceIndexes = sortArray(mv.referenceIds.map(id => dp.referenceIds.indexOf(id)));
        const dvs = ModuleFE_DiseaseValue.cache.filter(i => i.multiVarId === mv._id);
        return _jsxs(React.Fragment, { children: [_jsxs("h3", { children: [mainVar.name, referenceIndexes.map(i => _jsxs("sup", { children: ["[", i + 1, "]"] }, i))] }), dvs.map((dv, i) => {
                    return _jsxs(React.Fragment, { children: [this.renderDiseaseValue(mv, dv), i !== dvs.length - 1 && _jsx("div", { className: 'printable__disease-profile__value-separator' })] }, dv._id);
                })] }, mv._id);
    };
    renderDiseaseValue = (mv, dv) => {
        const mainValue = ModuleFE_Value.cache.unique(dv.values[mv._firstVarId]);
        const likelihood = this.renderDVLikelihood(dv);
        const severity = dv.severityFactor ? SeverityLabelMap[dv.severityFactor] : undefined;
        const columnIds = _keys(dv.values).filter(i => i !== mv._firstVarId);
        return _jsxs(_Fragment, { children: [_jsx("h4", { className: 'bold', children: "Value" }), _jsx("p", { children: !mainValue ? '-' : resolveValueDisplay(mainValue) }), columnIds.map(varId => {
                    const variable = ModuleFE_Variable.cache.unique(varId);
                    const value = ModuleFE_Value.cache.unique(dv.values[varId]);
                    if (!variable) {
                        console.log(`NO VAR ${varId}`);
                        console.log(dv);
                    }
                    return _jsxs(React.Fragment, { children: [_jsx("h4", { children: variable.name }), _jsx("p", { children: resolveValueDisplay(value) })] }, varId);
                }), likelihood && _jsxs(_Fragment, { children: [_jsx("h4", { children: "Likelihood" }), _jsx("p", { children: likelihood })] }), severity && _jsxs(_Fragment, { children: [_jsx("h4", { children: "Severity" }), _jsx("p", { children: severity })] })] });
    };
    renderDVLikelihood = (dv) => {
        if (!dv.likelihood && !dv.likelihoodRange)
            return undefined;
        let str = '';
        if (dv.likelihoodRange) {
            str = this.numberArrayRenderer(dv.likelihoodRange) + '%';
        }
        if (dv.likelihood) {
            if (str !== '')
                str += ', ';
            str += LikelihoodLabelMap[dv.likelihood];
        }
        return str;
    };
}
//# sourceMappingURL=Printable_DiseaseProfile.js.map